/**
 * Environment configuration and utilities
 *
 * This module provides type-safe access to environment variables
 * and application configuration.
 */

// Safe browser detection that works in all environments
const browser = typeof window !== 'undefined';

// Environment variable interface
interface AppEnv {
	NODE_ENV: string;
	MODE: string;

	// API Configuration
	VITE_API_URL: string;
	VITE_USE_MOCKS: boolean;

	// Authentication Configuration (New Flow)
	VITE_AUTH_LOGIN_URL: string;
	VITE_AUTH_TOKEN_URL: string;
	VITE_AUTH_TOKEN_REFRESH_URL: string;
	VITE_AUTH_REDIRECT_URL: string;
	VITE_NATIVE_AUTH_REDIRECT_URL: string;
	VITE_AUTH_LOGOUT_REDIRECT_URL: string;
	VITE_AUTH_LOGIN_TYPE: string;
	VITE_AUTH_DEFAULT_VARIANT: string;
	VITE_AUTH_AB_TEST_ENABLED: boolean;
	VITE_AUTH_AB_TEST_RATIO: number;
	VITE_MOCK_AUTH_ENABLED: boolean;
	VITE_MOCK_JWT_SECRET: string;

	// Legacy (deprecated)
	VITE_AUTH_URL: string;

	// PWA Configuration
	VITE_APP_NAME: string;
	VITE_APP_SHORT_NAME: string;
	VITE_APP_DESCRIPTION: string;

	// Feature Flags
	VITE_ENABLE_CAMERA: boolean;
	VITE_ENABLE_BIOMETRICS: boolean;
	VITE_ENABLE_PUSH_NOTIFICATIONS: boolean;
	VITE_ENABLE_OFFLINE: boolean;

	// Development Tools
	VITE_ENABLE_DEBUG: boolean;
	VITE_ENABLE_MOCK_DATA: boolean;
	VITE_INCLUDE_DEV_ROUTES: boolean;

	// Eligibility Polling
	VITE_ELIGIBILITY_POLL_ENABLED: boolean;
	VITE_ELIGIBILITY_POLL_INTERVAL: number;

	// Web Portal
	VITE_WEB_PORTAL_URL: string;

	// Analytics (optional)
	VITE_ANALYTICS_ID?: string;
	VITE_SENTRY_DSN?: string;

	// Capacitor Configuration
	CAPACITOR_APP_ID: string;
	CAPACITOR_APP_NAME: string;
}

/**
 * Get environment variable with type conversion
 */
function getEnvVar(key: string, defaultValue?: string): string {
	// Priority: 1. window.__APP_ENV__ (if key exists), 2. import.meta.env, 3. defaultValue
	if (browser && typeof window !== 'undefined' && (window as any).__APP_ENV__) {
		const runtimeValue = (window as any).__APP_ENV__[key];
		if (runtimeValue) {
			return runtimeValue;
		}
	}
	return import.meta.env[key] || defaultValue || '';
}

/**
 * Get boolean environment variable
 */
function getBooleanEnv(key: string, defaultValue: boolean = false): boolean {
	const value = getEnvVar(key);
	if (!value) return defaultValue;
	return value.toLowerCase() === 'true';
}

/**
 * Get number environment variable
 */
function getNumberEnv(key: string, defaultValue: number = 0): number {
	const value = getEnvVar(key);
	if (!value) return defaultValue;
	const parsed = parseFloat(value);
	return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Application environment configuration
 */
export const env: AppEnv = {
	NODE_ENV: getEnvVar('NODE_ENV', 'production'),
	MODE: getEnvVar('MODE', 'production'),

	// API Configuration
	VITE_API_URL: getEnvVar('VITE_API_URL', 'https://ms.xpansiv.com/app/api/v1'),
	VITE_USE_MOCKS: getBooleanEnv('VITE_USE_MOCKS', true),

	// Authentication Configuration (New Flow)
	VITE_AUTH_LOGIN_URL: getEnvVar('VITE_AUTH_LOGIN_URL', 'https://ms.xpansiv.com/app/auth/login'),
	VITE_AUTH_TOKEN_URL: getEnvVar('VITE_AUTH_TOKEN_URL', 'https://ms.xpansiv.com/app/auth/token'),
	VITE_AUTH_TOKEN_REFRESH_URL: getEnvVar(
		'VITE_AUTH_TOKEN_REFRESH_URL',
		'https://ms.xpansiv.com/app/auth/token_refresh'
	),
	VITE_AUTH_REDIRECT_URL: getEnvVar(
		'VITE_AUTH_REDIRECT_URL',
		'https://msmobile.xpansiv.com/auth/callback'
	),
	VITE_NATIVE_AUTH_REDIRECT_URL: getEnvVar(
		'VITE_NATIVE_AUTH_REDIRECT_URL',
		'xpansiv://auth/callback'
	),
	VITE_AUTH_LOGOUT_REDIRECT_URL: getEnvVar('VITE_AUTH_LOGOUT_REDIRECT_URL', ''),
	VITE_AUTH_LOGIN_TYPE: getEnvVar('VITE_AUTH_LOGIN_TYPE', 'mobile_app'),
	VITE_AUTH_DEFAULT_VARIANT: getEnvVar('VITE_AUTH_DEFAULT_VARIANT', 'redirect'),
	VITE_AUTH_AB_TEST_ENABLED: getBooleanEnv('VITE_AUTH_AB_TEST_ENABLED', false),
	VITE_AUTH_AB_TEST_RATIO: getNumberEnv('VITE_AUTH_AB_TEST_RATIO', 0.5),
	VITE_MOCK_AUTH_ENABLED: getBooleanEnv('VITE_MOCK_AUTH_ENABLED', false),
	VITE_MOCK_JWT_SECRET: getEnvVar('VITE_MOCK_JWT_SECRET', ''),

	// Legacy (deprecated - use VITE_AUTH_LOGIN_URL)
	VITE_AUTH_URL: getEnvVar('VITE_AUTH_URL', 'https://ms.xpansiv.com/app/auth/jwt'),

	// PWA Configuration
	VITE_APP_NAME: getEnvVar('VITE_APP_NAME', 'Xpansiv'),
	VITE_APP_SHORT_NAME: getEnvVar('VITE_APP_SHORT_NAME', 'Xpansiv'),
	VITE_APP_DESCRIPTION: getEnvVar(
		'VITE_APP_DESCRIPTION',
		'Solar Renewable Energy Credit trading mobile application'
	),

	// Feature Flags
	VITE_ENABLE_CAMERA: getBooleanEnv('VITE_ENABLE_CAMERA', true),
	VITE_ENABLE_BIOMETRICS: getBooleanEnv('VITE_ENABLE_BIOMETRICS', false),
	VITE_ENABLE_PUSH_NOTIFICATIONS: getBooleanEnv('VITE_ENABLE_PUSH_NOTIFICATIONS', true),
	VITE_ENABLE_OFFLINE: getBooleanEnv('VITE_ENABLE_OFFLINE', true),

	// Development Tools
	VITE_ENABLE_DEBUG: getBooleanEnv('VITE_ENABLE_DEBUG', false),
	VITE_ENABLE_MOCK_DATA: getBooleanEnv('VITE_ENABLE_MOCK_DATA', true),
	VITE_INCLUDE_DEV_ROUTES: getBooleanEnv('VITE_INCLUDE_DEV_ROUTES', false),

	// Eligibility Polling
	VITE_ELIGIBILITY_POLL_ENABLED: getBooleanEnv('VITE_ELIGIBILITY_POLL_ENABLED', true),
	VITE_ELIGIBILITY_POLL_INTERVAL: getNumberEnv('VITE_ELIGIBILITY_POLL_INTERVAL', 30000),

	// Web Portal
	VITE_WEB_PORTAL_URL: getEnvVar('VITE_WEB_PORTAL_URL', 'https://ms.xpansiv.com/app/facility/'),

	// Analytics (optional)
	VITE_ANALYTICS_ID: getEnvVar('VITE_ANALYTICS_ID'),
	VITE_SENTRY_DSN: getEnvVar('VITE_SENTRY_DSN'),

	// Capacitor Configuration
	CAPACITOR_APP_ID: getEnvVar('CAPACITOR_APP_ID', 'com.xpansiv.srectrade'),
	CAPACITOR_APP_NAME: getEnvVar('CAPACITOR_APP_NAME', 'Xpansiv')
};

/**
 * Environment helper functions
 */
export const isProduction = env.MODE === 'production';
export const isDevelopment = env.MODE === 'development';
export const isTest = env.MODE === 'test';

/**
 * Feature flags
 */
export const features = {
	camera: env.VITE_ENABLE_CAMERA,
	biometrics: env.VITE_ENABLE_BIOMETRICS,
	pushNotifications: env.VITE_ENABLE_PUSH_NOTIFICATIONS,
	offline: env.VITE_ENABLE_OFFLINE,
	debug: env.VITE_ENABLE_DEBUG,
	mockData: env.VITE_ENABLE_MOCK_DATA,
	includeDevRoutes: env.VITE_INCLUDE_DEV_ROUTES
} as const;

/**
 * API configuration
 */
export const api = {
	baseUrl: env.VITE_API_URL,
	tokenRefreshUrl: env.VITE_AUTH_TOKEN_REFRESH_URL,
	useMocks: env.VITE_USE_MOCKS,
	timeout: 30000
} as const;

/**
 * PWA configuration
 */
export const pwa = {
	name: env.VITE_APP_NAME,
	shortName: env.VITE_APP_SHORT_NAME,
	description: env.VITE_APP_DESCRIPTION
} as const;

/**
 * Capacitor configuration
 */
export const capacitor = {
	appId: env.CAPACITOR_APP_ID,
	appName: env.CAPACITOR_APP_NAME
} as const;

/**
 * Analytics configuration
 */
export const analytics = {
	id: env.VITE_ANALYTICS_ID,
	sentryDsn: env.VITE_SENTRY_DSN,
	enabled: !!(env.VITE_ANALYTICS_ID || env.VITE_SENTRY_DSN)
} as const;

/**
 * Authentication configuration
 */
export const auth = {
	// New two-step flow URLs
	loginUrl: env.VITE_AUTH_LOGIN_URL,
	tokenUrl: env.VITE_AUTH_TOKEN_URL,
	redirectUrl: env.VITE_AUTH_REDIRECT_URL,
	nativeRedirectUrl: env.VITE_NATIVE_AUTH_REDIRECT_URL,
	logoutRedirectUrl: env.VITE_AUTH_LOGOUT_REDIRECT_URL || env.VITE_AUTH_LOGIN_URL,
	loginType: env.VITE_AUTH_LOGIN_TYPE,
	defaultVariant: env.VITE_AUTH_DEFAULT_VARIANT as 'redirect' | 'popup' | 'modal' | 'iframe',
	abTestEnabled: env.VITE_AUTH_AB_TEST_ENABLED,
	abTestRatio: env.VITE_AUTH_AB_TEST_RATIO,
	mockEnabled: env.VITE_MOCK_AUTH_ENABLED,
	mockJwtSecret: env.VITE_MOCK_JWT_SECRET,

	// Legacy (deprecated)
	url: env.VITE_AUTH_URL
} as const;

/**
 * Eligibility polling configuration
 */
export const eligibilityPolling = {
	enabled: env.VITE_ELIGIBILITY_POLL_ENABLED,
	interval: env.VITE_ELIGIBILITY_POLL_INTERVAL
} as const;

/**
 * Web portal configuration
 */
export const webPortal = {
	url: env.VITE_WEB_PORTAL_URL
} as const;

/**
 * Debug utilities
 */
export const debug = {
	enabled: env.VITE_ENABLE_DEBUG,
	log: (...args: any[]) => {
		if (env.VITE_ENABLE_DEBUG) {
			console.log('[Xpansiv]', ...args);
		}
	},
	warn: (...args: any[]) => {
		if (env.VITE_ENABLE_DEBUG) {
			console.warn('[Xpansiv]', ...args);
		}
	},
	error: (...args: any[]) => {
		if (env.VITE_ENABLE_DEBUG) {
			console.error('[Xpansiv]', ...args);
		}
	}
} as const;

/**
 * Validate required environment variables
 */
export function validateEnvironment(): { valid: boolean; missing: string[] } {
	const requiredVars = ['VITE_API_URL', 'VITE_APP_NAME', 'CAPACITOR_APP_ID'];

	const missing = requiredVars.filter((key) => !getEnvVar(key));

	return {
		valid: missing.length === 0,
		missing
	};
}

// Log environment validation in development
if (isDevelopment && browser) {
	const validation = validateEnvironment();
	if (!validation.valid) {
		debug.warn('Missing required environment variables:', validation.missing);
	} else {
		debug.log('Environment validation passed');
	}
}
